import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fetchDataSources, deleteDataSource } from '../store/slices/dataSourcesSlice';
import { switchTenant } from '../store/slices/authSlice';
import { redirectToSalesforceAddDataSource, redirectToHubSpotAddDataSource } from '../api/auth';
import { createDataSource } from '../api/auth';
import { sanitizeDisplayError } from '../utils/sanitizeDisplayError';

// Data source configuration (provider-specific styling and content)
const DATA_SOURCE_CONFIG = {
  salesforce: {
    displayName: 'Salesforce',
    bgColor: 'bg-[#00A1E0]',
    orgLabel: 'Org',
    icon: (
      <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.87 3.97c1.21-.55 2.56-.84 3.93-.84 2.24 0 4.31.78 5.92 2.09-.63.62-1.17 1.32-1.61 2.11-1.24-1.07-2.75-1.72-4.47-1.72-1.98 0-3.77.82-5.04 2.15-1.27-1.33-3.06-2.15-5.04-2.15-1.87 0-3.54.76-4.77 1.99-.15-.84-.22-1.67-.22-2.51 0-1.97.41-3.84 1.15-5.53C4.33 1.14 6.44.44 8.69.44c2.3 0 4.36.9 5.91 2.35.28-.09.55-.18.84-.25-.54-.28-1.11-.52-1.7-.71-.62.36-1.26.69-1.87 1.14zm8.04 10.05c0 3.82-3.1 6.92-6.92 6.92-3.82 0-6.92-3.1-6.92-6.92s3.1-6.92 6.92-6.92 6.92 3.1 6.92 6.92z" />
      </svg>
    )
  },
  hubspot: {
    displayName: 'HubSpot',
    bgColor: 'bg-[#FF7A59]',
    orgLabel: 'Portal',
    icon: (
      <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.072A2.198 2.198 0 0017.233.836h-.071a2.198 2.198 0 00-2.199 2.198v.071a2.199 2.199 0 001.267 1.979v2.846a4.242 4.242 0 00-2.344.774l-6.164-3.287a2.477 2.477 0 10-.913 1.062l6.164 3.287a4.315 4.315 0 00-.549 2.093 4.346 4.346 0 004.345 4.345 4.346 4.346 0 004.345-4.345A4.346 4.346 0 0018.164 7.93z"/>
      </svg>
    )
  },
  file: {
    displayName: 'Uploaded File',
    bgColor: 'bg-gray-600',
    orgLabel: 'Tables',
    icon: (
      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    )
  }
};

// Get config for a data source type
export function getDataSourceConfig(sourceType) {
  return DATA_SOURCE_CONFIG[sourceType] || {
    displayName: sourceType || 'Data Source',
    bgColor: 'bg-gray-500',
    orgLabel: 'Account',
    icon: (
      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
      </svg>
    )
  };
}

// Data source icon component
export function DataSourceIcon({ sourceType }) {
  const config = getDataSourceConfig(sourceType);
  const isKnownType = Object.prototype.hasOwnProperty.call(DATA_SOURCE_CONFIG, sourceType);
  return (
    <div
      className={`w-12 h-12 ${config.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}
      data-testid={`data-source-icon-${sourceType || 'unknown'}`}
      data-icon-known={isKnownType ? 'true' : 'false'}
    >
      {config.icon}
    </div>
  );
}

// Status badge component for data sources
function DataSourceStatusBadge({ status }) {
  const styles = {
    active: 'bg-green-100 text-green-800',
    needs_reauth: 'bg-orange-100 text-orange-800',
    revoked: 'bg-red-100 text-red-800',
    none: 'bg-gray-100 text-gray-800',
  };

  const labels = {
    active: 'Active',
    needs_reauth: 'Needs Re-auth',
    revoked: 'Revoked',
    none: 'Not Connected',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.none}`}>
      {labels[status] || status}
    </span>
  );
}

// Format relative time
function formatRelativeTime(dateStr) {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Sync status badge
function SyncStatusBadge({ status }) {
  if (!status) return null;

  const styles = {
    running: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };

  const icons = {
    running: (
      <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    ),
    completed: (
      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
    failed: (
      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    ),
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || ''}`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// Modal to create a new BYOD data source (no OAuth — just name + table prefix)
function BYODCreateModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [tablePrefix, setTablePrefix] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const prefixRe = /^[a-z][a-z0-9_]*$/;
  const prefixError = tablePrefix && !prefixRe.test(tablePrefix)
    ? 'Use lowercase letters, digits, and underscores; must start with a letter.'
    : '';

  const canCreate = name.trim() && tablePrefix.trim() && !prefixError && !creating;

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await createDataSource({ name: name.trim(), table_prefix: tablePrefix.trim(), source_type: 'file' });
      onCreated();
    } catch (err) {
      setError(sanitizeDisplayError(err, 'Failed to create data source.'));
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="byod-create-modal-title"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 id="byod-create-modal-title" className="text-lg font-bold text-gray-900">
              Add BYOD Data Source
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Upload your own tables — no OAuth or external sync needed.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Marketing Data"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Table prefix
            </label>
            <input
              type="text"
              value={tablePrefix}
              onChange={(e) => setTablePrefix(e.target.value.toLowerCase())}
              placeholder="e.g. mktg"
              className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                prefixError ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {prefixError && (
              <p className="mt-1 text-xs text-red-600">{prefixError}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Tables will be queryable as <code className="bg-gray-100 px-1 rounded">{tablePrefix || 'prefix'}__tablename</code>.
            </p>
          </div>

          {/* Info card */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
            <p className="font-medium mb-1">BYOD (Bring Your Own Data)</p>
            <p>Upload JSON tables directly — no OAuth or external sync needed. Tables are instantly queryable via FrankenQL.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={creating}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!canCreate}
            className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="byod-create-confirm-button"
          >
            {creating && (
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {creating ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DataSourcesTab() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items: dataSources, isLoading, error } = useSelector((state) => state.dataSources);
  const { activeTenant, tenants } = useSelector((state) => state.auth);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showTenantMismatchModal, setShowTenantMismatchModal] = useState(null);
  const [switchingTenant, setSwitchingTenant] = useState(false);
  // BYOD
  const [showBYODModal, setShowBYODModal] = useState(false);
  const [expandedBYOD, setExpandedBYOD] = useState(null); // data_source_id

  // Get current tenant ID from active tenant in Redux state
  const currentTenantId = activeTenant?.tenant_id;

  // Check for tenant_mismatch URL parameter on mount
  useEffect(() => {
    const tenantMismatch = searchParams.get('tenant_mismatch');
    const otherTenantId = searchParams.get('other_tenant_id');
    const otherTenantName = searchParams.get('other_tenant_name');

    if (tenantMismatch === 'true' && otherTenantId && otherTenantName) {
      setShowTenantMismatchModal({
        tenantId: otherTenantId,
        tenantName: decodeURIComponent(otherTenantName),
      });
      // Clear the URL params
      searchParams.delete('tenant_mismatch');
      searchParams.delete('other_tenant_id');
      searchParams.delete('other_tenant_name');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    dispatch(fetchDataSources());
  }, [dispatch]);

  // Handle switching to the other tenant
  const handleSwitchTenant = async (tenantId) => {
    setSwitchingTenant(true);
    try {
      await dispatch(switchTenant(tenantId)).unwrap();
      setShowTenantMismatchModal(null);
      // Refresh the page to load the new tenant's data
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch tenant:', err);
      alert('Failed to switch tenant: ' + sanitizeDisplayError(err));
    } finally {
      setSwitchingTenant(false);
    }
  };

  const handleConnectSalesforce = () => {
    if (currentTenantId) {
      redirectToSalesforceAddDataSource(currentTenantId);
    }
  };

  const handleConnectHubSpot = () => {
    if (currentTenantId) {
      redirectToHubSpotAddDataSource(currentTenantId);
    }
  };

  const handleReconnect = (dataSource) => {
    if (!currentTenantId) return;
    if (dataSource.source_type === 'hubspot') {
      redirectToHubSpotAddDataSource(currentTenantId);
    } else {
      // Preserve the existing Salesforce behavior and use it as the fallback
      // for any provider without a dedicated reconnect route.
      redirectToSalesforceAddDataSource(currentTenantId);
    }
  };

  const handleDelete = async (dataSource) => {
    setDeleteLoading(true);
    try {
      await dispatch(deleteDataSource({ dataSourceId: dataSource.data_source_id })).unwrap();
      setShowDeleteModal(null);
    } catch (err) {
      console.error('Failed to delete data source:', err);
      alert(sanitizeDisplayError(err, 'Failed to delete data source'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchDataSources());
  };

  if (isLoading && dataSources.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">
        Loading data sources...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load data sources: {sanitizeDisplayError(error, 'Request failed')}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-sources-container">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="font-bold text-gray-900">Data Sources</h2>
            <p className="text-sm text-gray-500 mt-1">
              Connect and manage Salesforce or HubSpot data sources for your tenant
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Refresh
            </button>
            <button
              onClick={handleConnectSalesforce}
              disabled={!currentTenantId}
              className="px-4 py-2 text-sm font-medium text-white bg-[#00A1E0] rounded-lg hover:bg-[#0089C1] transition flex items-center gap-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
              </svg>
              Connect Salesforce
            </button>
            <button
              onClick={handleConnectHubSpot}
              disabled={!currentTenantId}
              className="px-4 py-2 text-sm font-medium text-white bg-[#FF5C35] rounded-lg hover:bg-[#FF7A59] transition flex items-center gap-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
              </svg>
              Connect HubSpot
            </button>
            <button
              onClick={() => setShowBYODModal(true)}
              disabled={!currentTenantId}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition flex items-center gap-2 disabled:opacity-50"
              data-testid="connect-byod-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              BYOD
            </button>
          </div>
        </div>
      </div>

      {/* Data Sources List */}
      {dataSources.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="max-w-md mx-auto">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No data sources connected</h3>
            <p className="text-sm text-gray-500 mb-6">
              Connect Salesforce or HubSpot to start syncing your CRM data.
              You can connect multiple data sources and query across them.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={handleConnectSalesforce}
                disabled={!currentTenantId}
                className="px-6 py-3 text-sm font-medium text-white bg-[#00A1E0] rounded-lg hover:bg-[#0089C1] transition disabled:opacity-50"
              >
                Connect Salesforce
              </button>
              <button
                onClick={handleConnectHubSpot}
                disabled={!currentTenantId}
                className="px-6 py-3 text-sm font-medium text-white bg-[#FF5C35] rounded-lg hover:bg-[#FF7A59] transition disabled:opacity-50"
              >
                Connect HubSpot
              </button>
              <button
                onClick={() => setShowBYODModal(true)}
                disabled={!currentTenantId}
                className="px-6 py-3 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
                data-testid="connect-byod-button"
              >
                BYOD (Bring Your Own Data)
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-4">
          {dataSources.map((ds) => (
            <div
              key={ds.data_source_id}
              className="bg-white rounded-lg shadow hover:shadow-md transition"
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Dynamic Data Source Icon */}
                    <DataSourceIcon sourceType={ds.source_type} />

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{ds.name}</h3>
                        <DataSourceStatusBadge status={ds.auth_status} />
                        {ds.last_sync_status && (
                          <SyncStatusBadge status={ds.last_sync_status} />
                        )}
                      </div>
                      <div className="text-sm text-gray-500 space-y-0.5">
                        {/* Dynamic org/portal label based on source type */}
                        {ds.metadata?.salesforce_org_name && ds.source_type === 'salesforce' && (
                          <p>{getDataSourceConfig('salesforce').orgLabel}: {ds.metadata.salesforce_org_name}</p>
                        )}
                        {ds.metadata?.hubspot_account_name && ds.source_type === 'hubspot' && (
                          <p>{getDataSourceConfig('hubspot').orgLabel}: {ds.metadata.hubspot_account_name}</p>
                        )}
                        <p className="font-mono text-xs text-gray-400">
                          Table prefix: {ds.table_prefix}__
                        </p>
                        <p className="text-xs">
                          Last sync: {formatRelativeTime(ds.last_sync_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {ds.auth_status === 'needs_reauth' && (
                      <button
                        onClick={() => handleReconnect(ds)}
                        disabled={!currentTenantId}
                        className="px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-100 rounded-lg hover:bg-orange-200 transition"
                      >
                        Reconnect
                      </button>
                    )}
                    {ds.source_type === 'byod' && (
                      <button
                        onClick={() =>
                          setExpandedBYOD((prev) =>
                            prev === ds.data_source_id ? null : ds.data_source_id
                          )
                        }
                        className="px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition"
                        data-testid={`byod-manage-${ds.data_source_id}`}
                      >
                        {expandedBYOD === ds.data_source_id ? 'Hide tables' : 'Manage tables'}
                      </button>
                    )}
                    <button
                      onClick={() => setShowDeleteModal(ds)}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>

            </div>
          ))}
        </div>
      )}

      {/* Multi-org Info Banner */}
      {dataSources.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm">
              <p className="font-medium text-blue-800">Multi-org queries</p>
              <p className="text-blue-700 mt-1">
                Tables from each data source are prefixed (e.g., <code className="bg-blue-100 px-1 rounded">{dataSources[0]?.table_prefix}__Account</code>).
                You can JOIN tables across data sources in your queries.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Remove Data Source</h3>
            <p className="text-sm text-gray-600 mb-2">
              Are you sure you want to remove <strong>{showDeleteModal.name}</strong>?
            </p>
            <p className="text-sm text-red-600 mb-4">
              This will delete all synced data for this {showDeleteModal && getDataSourceConfig(showDeleteModal.source_type).orgLabel.toLowerCase()}. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal)}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {deleteLoading ? 'Removing...' : 'Remove Data Source'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BYOD Create Modal */}
      {showBYODModal && (
        <BYODCreateModal
          onClose={() => setShowBYODModal(false)}
          onCreated={() => {
            setShowBYODModal(false);
            dispatch(fetchDataSources());
          }}
        />
      )}

      {/* Tenant Mismatch Modal */}
      {showTenantMismatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Data Source Already Connected</h3>
                <p className="text-sm text-gray-500 mt-1">This data source belongs to a different tenant</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700">
                The data source you tried to connect is already part of{' '}
                <strong className="text-gray-900">{showTenantMismatchModal.tenantName}</strong>.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Each data source can only be connected to one tenant. You can either:
              </p>
              <ul className="text-sm text-gray-600 mt-2 ml-4 list-disc">
                <li>Switch to <strong>{showTenantMismatchModal.tenantName}</strong> to access this data</li>
                <li>Stay in the current tenant and connect a different data source</li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Need to merge tenants?</p>
                  <p className="text-blue-700 mt-1">
                    Merging tenants is a complex operation that requires careful data migration.
                    Contact us and we can help merge your tenants safely.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowTenantMismatchModal(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Stay Here
              </button>
              {tenants?.some(t => t.tenant_id === showTenantMismatchModal.tenantId) && (
                <button
                  onClick={() => handleSwitchTenant(showTenantMismatchModal.tenantId)}
                  disabled={switchingTenant}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
                >
                  {switchingTenant ? 'Switching...' : `Switch to ${showTenantMismatchModal.tenantName}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataSourcesTab;
